import Loader from 'components/Loader';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetTopClientsByRevenueReportQuery } from 'store/api/reportingExecutive';
import { PartyRevenueTotals, TopClientsByRevenueRow } from 'types/reporting/executive';
import { ReportPage } from 'types/reporting/reportPage';
import { useClassifiers } from 'utils/useClassifiers';
import { clientFilter } from '../reportFilters';
import { useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';
import {
    buildPartyRevenueColumns,
    buildPartyRevenueFilterConfig,
    PARTY_REVENUE_CLASSIFIERS,
    PartyColumns,
} from './partyRevenueTable';

type Row = TopClientsByRevenueRow;

const REPORT_KEY = 'top-clients-by-revenue';

const CLIENT_COLUMNS: PartyColumns<Row> = {
    numberId: 'clientNumber',
    numberLabelId: 'reporting.column.client-number',
    nameId: 'clientName',
    nameLabelId: 'reporting.column.client-name',
    partyId: (row) => row.clientId,
    partyNumber: (row) => row.clientNumber,
    partyName: (row) => row.clientName,
};

export function TopClientsByRevenueReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetTopClientsByRevenueReportQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: PARTY_REVENUE_CLASSIFIERS });
    const filterConfig = useMemo(
        () => buildPartyRevenueFilterConfig(clientFilter(), ['clientNumber', 'clientName'], classifiers),
        [classifiers]
    );
    const currency = useReportBaseCurrency(data?.totals?.baseCurrency ?? data?.content[0]?.baseCurrency);
    const columns = useMemo(() => buildPartyRevenueColumns(data?.content, CLIENT_COLUMNS, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            {isLoadingClassifiers ? (
                <Loader />
            ) : (
                <ListTableWithFilters<Row, ReportPage<Row, PartyRevenueTotals>>
                    data={data}
                    belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                    isLoading={isFetching}
                    isError={isError}
                    fetchData={fetchData}
                    filterConfig={filterConfig}
                    columns={columns}
                    columnConfigId={`report-${REPORT_KEY}`}
                    enableColumnPicker
                    enableColumnReorder
                    enableColumnResize
                    pluralTextId="reporting.clients"
                    singularTextId="reporting.client"
                    noEntriesId="reporting.no-rows"
                    noEntriesBodyId="reporting.no-rows-body"
                />
            )}
        </ReportLayout>
    );
}
